/**
 *  @file       address-collection.js
 *  @author     Eddie Roosenmaallen, eddie@kingsds.network
 *  @date       December 2019
 *
 *  This module exports a class to manage heaps of DCP addresses
 *  with efficient lookup and management.
 */

const { Address, isAddress } = require('./eth');

const INTERNAL = Symbol.for('AddressCollection Internals');
const ANY_STRINGS = ['any', '*'];

/**
 *  Stores a collection of addresses optimized for fast lookup
 */
class AddressCollection {
  /** Construct a new AddressCollection
   *  @param    startWith       Optional initial address(es); may be a single Address-shaped object or 
   *                            string, or an array thereof.
   */
  constructor(startWith) {
    this[INTERNAL] = {
      heap: {},
    };

    // If the list contains the strings 'any' or '*'
    // then the includes method will always return true.
    this.matchAny = false;
    
    if (isAddress(startWith) || Array.isArray(startWith)) {
      this.add(startWith);
    }
  }
  
  /** Query the heap for an Address
   *  @param    address         address to query; may be:
   *                            - string, 0x prefix optional
   *                            - a DCP Address object
   *                            - buffer(ish)
   *  @return   TRUE if the address is present, FALSE otherwise
   */
  includes(address) {
    const addr = new Address(address).valueOf();
    
    return this[INTERNAL].heap[addr] || this.matchAny;
  }

  ct(address) {
    return this.includes(address);
  }

  /** Add one or more addresses to the heap
   *  @param    address         address to add; may be
   *                            - string, 0x prefix optional
   *                            - a DCP Address object
   *                            - a Promise which resolves to an instance of Address
   *                            - buffer(ish)
   *                            - one of the wildcard strings (*, any)
   *                            - an array thereof
   *  @return   this, to support chaining
   */
  async add(input) {
    if (Array.isArray(input)) {
      input.forEach(addr => this.add(addr));
      return this;
    }
    let address = input;
    // Check if input is an identity promise
    if (input instanceof Promise) {
      let res = await input;
      address = res.address.valueOf()
    }
    
    if (isAddress(address)) {
      const addr = new Address(address).valueOf();
      this[INTERNAL].heap[addr] = true;
    } else if (typeof address === 'string' && ANY_STRINGS.includes(address)) {
      this.matchAny = true;
    }
    
    return this;
  }
  
  /** Remove an address from the heap
   *  @param    address         address to add; may be
   *                            - string, 0x prefix optional
   *                            - a DCP Address object
   *                            - buffer(ish)
   *                            - an array thereof
   *  @return   this, to support chaining
   */
  remove(address) {
    if (Array.isArray(address)) {
      address.forEach(addr => isAddress(addr) && this.remove(addr));
      return this;
    }
    
    if (isAddress(address)) {
      const addr = new Address(address).valueOf();
      delete this[INTERNAL].heap[addr];
    }
    
    return this;
  }
  
  /** Clear all addresses
   */
  clear() {
    this[INTERNAL].heap = {};
    this.matchAny = false;
    
    return this;
  }
  
  /** Report the number of addresses in this heap
   *  (perhaps not useful? Maybe just an `.isEmpty()` member?)
   */
  get length() {
    return this.all.length;
  }
  
  /** Return an array of addresses in this heap
   */
  get all() {
    return Object.keys(this[INTERNAL].heap);
  }
}

Object.assign(exports, { 
  AddressCollection
});
